import React from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useIsMounted from '../../hooks/useIsMounted.mjs';
import LikeDislikeButtons from '../../components/reactions/LikeDislikeButtons.mjs';
import { buildArticleUrl, buildContextualArticleUrl } from '../../utils/buildArticleUrl.mjs';
import ShareOnX from '../../components/social/ShareOnX.mjs';
import StarRating from '../../components/ratings/StarRating.mjs';
import { setConversationExpanded } from '../../slices/conversationSlice.mjs';
import NcaaOverlaySvg from '../../components/decorative/NcaaOverlaySvg.mjs';

// Helper Functions
const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            timeZoneName: 'short',
        }).format(date);
    } catch (e) {
        console.error('Error formatting date:', dateString, e);
        return '';
    }
};

const getExcerpt = (text, wordCount = 40) => {
    if (!text || typeof text !== 'string') return '';
    const words = text.split(' ');
    if (words.length <= wordCount) return text;
    return words.slice(0, wordCount).join(' ') + '...';
};

// Article Link Wrapper Component
function ArticleLinkWrapper({ article, slug, articleId, className, children, linkContext }) {
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const isConversationExpanded = useSelector(
        (state) => state.conversationSlice.isConversationExpanded
    );
    // Use contextual URL when linkContext provided (topic/category pages),
    // otherwise fall back to canonical URL (home page)
    const targetUrl = (article
        ? (linkContext ? buildContextualArticleUrl(article, linkContext) : buildArticleUrl(article))
        : null
    ) || `/q/articles/${slug || articleId}`;

    const handleArticleLinkClick = (e) => {
        // If conversation is expanded, collapse it when user clicks article link
        if (isConversationExpanded) {
            dispatch(setConversationExpanded(false));
        }
    };

    if (isMounted) {
        return (
            <Link to={targetUrl} state={{ type: 'article' }} className={className} onClick={handleArticleLinkClick}>
                {children}
            </Link>
        );
    } else {
        return (
            <a href={targetUrl} className={className} onClick={handleArticleLinkClick}>
                {children}
            </a>
        );
    }
}

// Chat Button Component — navigates to article detail with conversation open.
// Must remain a <button> (not <a>/<Link>) because it's nested inside
// ArticleLinkWrapper's <a> tag — HTML forbids <a> inside <a>.
// Uses pushState + popstate to trigger React Router client-side navigation.
function ChatButton({ article }) {
    const isMounted = useIsMounted();
    const { isAuthenticated } = useSelector((state) => state.auth);

    const chatUrl = article ? `${buildArticleUrl(article)}?chat=open` : null;
    const isDisabled = !isAuthenticated || !chatUrl;

    const title = !isAuthenticated
        ? 'Log in to access chat'
        : 'Open article with chat expanded';

    const handleChatClick = (e) => {
        // Prevent the parent ArticleLinkWrapper <a> from navigating
        e.stopPropagation();
        e.preventDefault();

        if (isDisabled || !isMounted) return;

        // SPA navigation: pushState changes the URL, then a synthetic popstate
        // event notifies React Router to match the new route client-side
        window.history.pushState({}, '', chatUrl);
        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    return (
        <button
            onClick={handleChatClick}
            disabled={isDisabled}
            className={`home-chat-button ${isDisabled ? 'disabled' : ''}`}
            aria-label="Expand chat for this article"
            title={title}
        >
            <svg
                className="home-chat-button-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                width="16"
                height="16"
            >
                <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
            </svg>
        </button>
    );
}

// Main Component
export default function HomePageContent({
    articlesGroupedByCategory,
    pagination,
    onPageChange,
    linkContext,
}) {
    const isMounted = useIsMounted();

    // Flatten the grouped articles into a single list with safety checks
    // Deduplicate by _id since same article can appear in multiple categories
    const allArticles = (() => {
        if (!Array.isArray(articlesGroupedByCategory)) return [];

        const articleMap = new Map();
        articlesGroupedByCategory.forEach((group) => {
            if (group && Array.isArray(group.articles)) {
                group.articles.forEach((article) => {
                    // IMPORTANT: Convert _id to string for Map key comparison.
                    // On SSR, _id is a Mongoose ObjectId (object reference) — Map.has()
                    // uses reference equality, so two ObjectIds with the same value
                    // would NOT match, causing duplicates in the flattened list.
                    // On the client (after JSON round-trip), _id is already a string,
                    // so dedup works correctly. This mismatch caused different article
                    // counts between SSR and client → hydration mismatch.
                    const idKey = article?._id?.toString?.() ?? article?._id;
                    if (idKey && !articleMap.has(idKey)) {
                        articleMap.set(idKey, article);
                    }
                });
            }
        });
        return Array.from(articleMap.values());
    })();

    const featuredArticle = allArticles.length > 0 ? allArticles[0] : null;
    const gridArticles = allArticles.slice(1);

    const showPagination = pagination && pagination.totalPages > 1;

    return (
        <div className="home-news-container">
            {/* Featured Article */}
            {featuredArticle && (
                <ArticleLinkWrapper article={featuredArticle} slug={featuredArticle.slug} articleId={featuredArticle._id} className="home-featured-link" linkContext={linkContext}>
                    <article className="home-featured-article">
                        <div className="home-featured-content">
                            <div className="home-featured-text">
                                <NcaaOverlaySvg variant={0} />
                                <div className="home-article-meta">
                                    {featuredArticle.baseCategory && (
                                        <span className="home-article-category">
                                            /{featuredArticle.baseCategory}
                                        </span>
                                    )}
                                </div>
                                <h1 className="home-featured-title">{featuredArticle.title}</h1>
                                <p className="home-featured-excerpt">
                                    {getExcerpt(
                                        featuredArticle.summary || featuredArticle.body,
                                        60
                                    )}
                                </p>
                                <div className="home-featured-reactions">
                                    <LikeDislikeButtons
                                        articleId={featuredArticle._id}
                                        variant="card"
                                        showCounts={true}
                                    />
                                    <ChatButton article={featuredArticle} />
                                    <ShareOnX article={featuredArticle} title={featuredArticle.title} />
                                    <StarRating articleId={featuredArticle._id} variant="card" />
                                </div>
                            </div>
                            {(featuredArticle.generatedImages?.[0]?.url ||
                                featuredArticle.images?.[0]?.url) && (
                                    <div className="home-featured-image-container">
                                        <img
                                            src={`${featuredArticle.generatedImages?.[0]?.url || featuredArticle.images?.[0]?.url}`}
                                            alt={
                                                (
                                                    featuredArticle.generatedImages?.[0] ||
                                                    featuredArticle.images?.[0]
                                                )?.alt || featuredArticle.title
                                            }
                                            className="home-featured-image"
                                        />
                                    </div>
                                )}
                        </div>
                    </article>
                </ArticleLinkWrapper>
            )}

            {/* Articles Grid */}
            <div className="home-articles-grid">
                {gridArticles.map((article) =>
                    article?._id ? (
                        <ArticleLinkWrapper
                            article={article}
                            slug={article.slug}
                            articleId={article._id}
                            className="home-article-link"
                            key={article._id.toString()}
                            linkContext={linkContext}
                        >
                            <article className="home-article-card">
                                {(article.generatedImages?.[0]?.url ||
                                    article.images?.[0]?.url) && (
                                        <div className="home-article-image-container">
                                            <img
                                                src={`${article.generatedImages?.[0]?.url || article.images?.[0]?.url}`}
                                                alt={
                                                    (
                                                        article.generatedImages?.[0] ||
                                                        article.images?.[0]
                                                    )?.alt || article.title
                                                }
                                                className="home-article-image"
                                            />
                                        </div>
                                    )}
                                <div className="article-card-content">
                                    <NcaaOverlaySvg variant={gridArticles.indexOf(article) % 6} />
                                    <div className="home-article-meta">
                                        {article.baseCategory && (
                                            <span className="home-article-category">
                                                /{article.baseCategory}
                                            </span>
                                        )}
                                    </div>
                                    <h2 className="article-title">{article.title}</h2>
                                    <p className="article-excerpt">
                                        {getExcerpt(article.summary || article.body)}
                                    </p>
                                    <div className="article-card-reactions">
                                        <LikeDislikeButtons
                                            articleId={article._id}
                                            variant="card"
                                            showCounts={true}
                                        />
                                        <ChatButton article={article} />
                                        <ShareOnX article={article} title={article.title} />
                                        <StarRating articleId={article._id} variant="card" />
                                    </div>
                                </div>
                            </article>
                        </ArticleLinkWrapper>
                    ) : null
                )}
            </div>

            {/* Pagination Controls */}
            {
                showPagination && (
                    <div className="home-pagination">
                        <span className="home-pagination-info">
                            Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <div className="home-pagination-buttons">
                            <button
                                onClick={() => onPageChange?.(pagination.page - 1)}
                                disabled={pagination.page <= 1}
                                className="home-pagination-btn"
                            >
                                ← Previous
                            </button>
                            <button
                                onClick={() => onPageChange?.(pagination.page + 1)}
                                disabled={pagination.page >= pagination.totalPages}
                                className="home-pagination-btn"
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                )
            }
        </div >
    );
}
